/**
 * LanguageToggle — Iter. 406.fr.56
 * ─────────────────────────────────
 * Compact 3-badge pill (FR · EN · ES) shown on the auth screen so a visitor
 * can override the browser-detected language BEFORE creating their account.
 *
 * On click: calls the AppContext `changeLanguage()` — which persists the
 * choice, updates i18next, and dispatches the `yumix-user-language-changed`
 * event so every subsequent screen (Welcome, NDA, Onboarding…) renders in
 * the chosen language from the very first paint.
 */
import React from 'react';
import { useLanguage } from '../context/AppContext';

const LANGS = [
  { code: 'fr', label: 'FR' },
  { code: 'en', label: 'EN' },
  { code: 'es', label: 'ES' },
];

const LanguageToggle = ({ className = '' }) => {
  const { language, changeLanguage } = useLanguage();
  return (
    <div
      className={`inline-flex items-center gap-1 rounded-full p-1 ${className}`}
      role="group"
      aria-label="Language selector"
      data-testid="language-toggle"
      style={{
        background: 'linear-gradient(135deg, rgba(0,0,0,0.7) 0%, rgba(15,15,15,0.65) 45%, rgba(251,146,60,0.14) 100%)',
        border: '1px solid rgba(251,146,60,0.42)',
        boxShadow: '0 0 14px rgba(251,146,60,0.32), inset 0 1px 0 rgba(255,255,255,0.06)',
        backdropFilter: 'blur(8px)',
      }}
    >
      {LANGS.map(({ code, label }) => {
        const active = language === code;
        return (
          <button
            key={code}
            type="button"
            onClick={() => changeLanguage(code)}
            className={`
              text-[11px] font-heading font-black uppercase tracking-widest
              rounded-full px-2.5 py-1 transition-all
              ${active
                ? 'text-white'
                : 'text-orange-200/60 hover:text-orange-100'}
            `}
            style={active ? {
              background: 'linear-gradient(140deg, #fb923c 0%, #f97316 55%, #c2410c 100%)',
              boxShadow: '0 0 14px rgba(251,146,60,0.7), inset 0 1px 0 rgba(255,255,255,0.25)',
              textShadow: '0 0 6px rgba(255,255,255,0.55)',
            } : undefined}
            aria-pressed={active}
            data-testid={`language-toggle-${code}`}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
};

export default LanguageToggle;
